import { forwardRef, useId, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { AlertTriangle, CheckCircle2, Info, Loader2, XCircle } from 'lucide-react';

export function cx(...classes: (string | false | null | undefined)[]) {
  return classes.filter(Boolean).join(' ');
}

/* ───────── Marca ───────── */

/**
 * Ícone oficial do Garlic (o mesmo de public/garlic.svg), redesenhado a partir da arte enviada pelo dono:
 * bulbo branco de três gomos sobre o quadrado roxo da marca.
 */
const BULB = 'M44.6 14.8h10.8c-.3 3.2-.4 5.4 0 8.3 1 9 6.9 15 14.2 20.5 7.3 5.5 11.4 11.6 11.4 19.4 0 10.8-8.4 19-19.2 19.2-4.6 2.6-17 2.6-21.6 0-10.8-.2-19.2-8.4-19.2-19.2 0-7.8 4.1-13.9 11.4-19.4 7.3-5.5 13.2-11.5 14.2-20.5.4-2.9.3-5.1 0-8.3z';
const CLOVES = 'M45 35c-2.6 9.6-13.4 16.6-13.4 29 0 9.4 3.4 15 7.8 18-3-5-4.9-10.3-4.4-19 .6-11 8.4-17.6 10-28zM55 35c2.6 9.6 13.4 16.6 13.4 29 0 9.4-3.4 15-7.8 18 3-5 4.9-10.3 4.4-19-.6-11-8.4-17.6-10-28z';
export const BRAND_PURPLE = '#55147F';

export function GarlicMark({ className = 'size-8' }: { className?: string }) {
  return (
    <svg viewBox="0 0 100 100" className={className} aria-hidden="true">
      <rect width="100" height="100" rx="22" fill={BRAND_PURPLE} />
      <path d={BULB} fill="#fff" />
      <path d={CLOVES} fill={BRAND_PURPLE} />
    </svg>
  );
}

/**
 * Marca do Garlic: ícone + "garlic" em serifa pesada (Fraunces com contraste de título) e, opcionalmente,
 * o slogan "Tecnologia para restaurantes" (página inicial).
 */
export function GarlicLogo({ suffix, tagline = false, size = 'md' }: { suffix?: string; tagline?: boolean; size?: 'md' | 'lg' }) {
  const lg = size === 'lg';
  return (
    <span className={cx('inline-flex items-center', lg ? 'gap-3.5' : 'gap-2.5')}>
      <GarlicMark className={lg ? 'size-12 sm:size-14' : 'size-9'} />
      <span className="flex flex-col leading-none">
        <span
          className={cx('font-display font-extrabold tracking-[-0.03em] text-ink', lg ? 'text-[2rem] sm:text-[2.4rem]' : 'text-[1.55rem]')}
          style={{ fontVariationSettings: "'SOFT' 0, 'WONK' 0, 'opsz' 144" }}
        >
          garlic
        </span>
        {tagline && <span className={cx('whitespace-nowrap font-medium text-garlic-500', lg ? 'mt-1 text-[0.8rem] sm:text-sm' : 'mt-0.5 text-[0.7rem]')}>Tecnologia para restaurantes</span>}
      </span>
      {suffix && <span className="rounded-md bg-ink px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-paper">{suffix}</span>}
    </span>
  );
}

/* ───────── Botões ───────── */

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'brand';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  icon?: ReactNode;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading, icon, children, className, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx(
        'inline-flex select-none items-center justify-center gap-2 rounded-xl font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-55',
        size === 'sm' && 'h-8 px-3 text-sm',
        size === 'md' && 'h-10 px-4 text-sm',
        size === 'lg' && 'h-12 px-5 text-base',
        variant === 'primary' && 'bg-garlic-600 text-white hover:bg-garlic-700',
        variant === 'brand' && 'bg-[var(--brand)] text-[var(--brand-contrast)] hover:brightness-95',
        variant === 'secondary' && 'border border-line bg-white text-ink hover:bg-paper-2',
        variant === 'ghost' && 'text-ink-2 hover:bg-paper-2 hover:text-ink',
        variant === 'danger' && 'bg-danger-600 text-white hover:brightness-95',
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : icon}
      {children}
    </button>
  );
});

/* ───────── Formulários ───────── */

export function Field({
  label,
  error,
  hint,
  children,
  className,
}: {
  label: string;
  error?: string;
  hint?: ReactNode;
  children: (id: string, describedBy?: string) => ReactNode;
  className?: string;
}) {
  const id = useId();
  const descId = error || hint ? `${id}-desc` : undefined;
  return (
    <div className={cx('flex flex-col gap-1.5', className)}>
      <label htmlFor={id} className="text-sm font-medium text-ink-2">
        {label}
      </label>
      {children(id, descId)}
      {(error || hint) && (
        <p id={descId} className={cx('text-xs', error ? 'text-danger-600' : 'text-ink-3')}>
          {error ?? hint}
        </p>
      )}
    </div>
  );
}

const inputBase =
  'w-full rounded-xl border bg-white px-3.5 text-[15px] text-ink placeholder:text-ink-3/70 transition-colors focus:border-garlic-500 focus:outline-none focus:ring-4 focus:ring-garlic-100 disabled:bg-paper-2';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }>(function Input(
  { className, invalid, ...rest },
  ref,
) {
  return <input ref={ref} aria-invalid={invalid || undefined} className={cx(inputBase, 'h-11', invalid ? 'border-danger-600' : 'border-line', className)} {...rest} />;
});

export function Select({ className, invalid, ...rest }: SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean }) {
  return <select aria-invalid={invalid || undefined} className={cx(inputBase, 'h-11 pr-8', invalid ? 'border-danger-600' : 'border-line', className)} {...rest} />;
}

export function Textarea({ className, invalid, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }) {
  return <textarea aria-invalid={invalid || undefined} className={cx(inputBase, 'min-h-24 py-2.5', invalid ? 'border-danger-600' : 'border-line', className)} {...rest} />;
}

/* ───────── Superfícies ───────── */

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx('rounded-2xl border border-line bg-white shadow-card', className)}>{children}</div>;
}

export function CardHeader({ title, description, action }: { title: string; description?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line px-5 py-4">
      <div>
        <h2 className="font-semibold text-ink">{title}</h2>
        {description && <p className="mt-0.5 text-sm text-ink-3">{description}</p>}
      </div>
      {action}
    </div>
  );
}

export function PageHeader({ title, description, action, eyebrow }: { title: string; description?: ReactNode; action?: ReactNode; eyebrow?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        {eyebrow && <div className="mb-1 text-sm text-ink-3">{eyebrow}</div>}
        <h1 className="font-display text-3xl font-semibold tracking-tight text-ink">{title}</h1>
        {description && <p className="mt-1 text-ink-3">{description}</p>}
      </div>
      {action}
    </div>
  );
}

const badgeTones = {
  neutral: 'bg-paper-2 text-ink-2',
  success: 'bg-sprout-100 text-sprout-600',
  warning: 'bg-warn-50 text-warn-600',
  danger: 'bg-danger-50 text-danger-600',
  brand: 'bg-garlic-50 text-garlic-600',
};

export function Badge({ tone = 'neutral', children, dot }: { tone?: keyof typeof badgeTones; children: ReactNode; dot?: boolean }) {
  return (
    <span className={cx('inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium', badgeTones[tone])}>
      {dot && <span className="size-1.5 rounded-full bg-current" />}
      {children}
    </span>
  );
}

export function Spinner({ label = 'Carregando…' }: { label?: string }) {
  return (
    <div role="status" className="flex items-center justify-center gap-2 py-12 text-ink-3">
      <Loader2 className="size-5 animate-spin" aria-hidden />
      <span className="text-sm">{label}</span>
    </div>
  );
}

const alertStyles = {
  info: { cls: 'border-garlic-200 bg-garlic-50 text-garlic-900', Icon: Info },
  success: { cls: 'border-sprout-500/30 bg-sprout-100 text-sprout-600', Icon: CheckCircle2 },
  warning: { cls: 'border-warn-600/25 bg-warn-50 text-warn-600', Icon: AlertTriangle },
  danger: { cls: 'border-danger-600/25 bg-danger-50 text-danger-600', Icon: XCircle },
};

export function Alert({ tone = 'info', title, children }: { tone?: keyof typeof alertStyles; title?: string; children?: ReactNode }) {
  const { cls, Icon } = alertStyles[tone];
  return (
    <div role={tone === 'danger' ? 'alert' : 'status'} className={cx('flex gap-3 rounded-xl border px-4 py-3 text-sm', cls)}>
      <Icon className="mt-0.5 size-4 shrink-0" aria-hidden />
      <div className="min-w-0">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className={cx(title && 'mt-0.5', 'break-words')}>{children}</div>}
      </div>
    </div>
  );
}

export function EmptyState({ icon, title, children, action }: { icon?: ReactNode; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center px-6 py-14 text-center">
      {icon && <div className="mb-3 grid size-12 place-items-center rounded-2xl bg-paper-2 text-ink-3">{icon}</div>}
      <p className="font-semibold text-ink">{title}</p>
      {children && <p className="mt-1 max-w-sm text-sm text-ink-3">{children}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}
